import { styled } from '@mui/material/styles';
import { WidgetStyle } from 'api/types';
import { MODAL_BORDER_RADIUS } from 'embed/constants';

interface ContainerProps {
  isBuilderPreview: boolean;
  isMobileSDK: boolean;
  legibleButtonOutlineColor: string;
  widgetStyle: WidgetStyle;
}

const generateContainerStyles = ({
  isBuilderPreview,
  isMobileSDK,
  widgetStyle,
}: ContainerProps) => {
  const modalContainerLeftBorder = `
    height: 100%;
    margin: 0;
    width: 100%;
    border-bottom: 0;
    border-radius: 0;
    border-right: 0;
    border-top: 0;
  `;

  const modalContainerMobileSDK = `
    border: 0;
    border-radius: 0;
    box-shadow: none;
    height: 100%;
    margin: 0;
    width: 100%;
  `;

  const modalContainerNoBorderRadius = `
    border-radius: 0;
  `;

  if (isMobileSDK) {
    return modalContainerMobileSDK;
  }
  if (widgetStyle !== 'mobile' && isBuilderPreview) {
    return modalContainerLeftBorder;
  } else if (widgetStyle === 'mobile' && !isBuilderPreview) {
    return modalContainerNoBorderRadius;
  }
};

const getBoxShadow = ({
  legibleButtonOutlineColor,
  widgetStyle,
}: ContainerProps) => {
  const defaultShadow = '0 12px 24px rgba(0, 0, 0, 0.08)';

  if (widgetStyle === 'floating_bar') {
    return `0 0 0 1px ${legibleButtonOutlineColor}, ${defaultShadow}`;
  } else {
    return defaultShadow;
  }
};

export const Container = styled('div')<ContainerProps>`
  align-items: center;
  background-color: ${props => props.theme.palette.colors.white};
  border-radius: ${MODAL_BORDER_RADIUS};
  box-shadow: ${getBoxShadow};
  display: flex;
  flex-direction: column;
  height: ${props =>
    props.widgetStyle === 'mobile' ? '100%' : 'calc(100% - 24px * 2)'};
  margin: ${props => (props.widgetStyle === 'mobile' ? '0' : '24px')};
  width: ${props =>
    props.widgetStyle === 'mobile' ? '100%' : 'calc(100% - 24px * 2)'};

  &.App-Modal-enter {
    opacity: 0;
  }
  &.App-Modal-exit {
    opacity: 0;
  }
  &.App-Modal-enter-done {
    opacity: 1;
  }

  pre {
    max-width: ${props => props.widgetStyle === 'mobile' && '88vw'};
  }
  ${generateContainerStyles}
`;

export const ContentContainer = styled('main')<{
  includeBorderRadius: boolean;
}>`
  background-color: ${props => props.theme.palette.colors.white};
  border-radius: ${props =>
    props.includeBorderRadius
      ? `${MODAL_BORDER_RADIUS} ${MODAL_BORDER_RADIUS} 0 0`
      : '0'};
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow-x: hidden; // Fix dynamic cards carousel breaking layout
`;
export const ModalHeader = styled('header')<{ noBorderRadius: boolean }>`
  align-items: center;
  background-color: var(--theme);
  border-radius: ${props =>
    props.noBorderRadius
      ? '0'
      : `${MODAL_BORDER_RADIUS} ${MODAL_BORDER_RADIUS} 0 0`};
  box-sizing: border-box;
  color: var(--legible-text-color);
  display: flex;
  height: 64px;
  justify-content: space-between;
  min-height: 64px; // this is required
  padding: 0 16px;
  width: 100%;

  &:focus {
    outline: none;
  }
`;

export const ModalHeaderText = styled('span')<{ hasBackButton?: boolean }>`
  font-weight: 600;
  margin-left: 8px;
  font-size: 14px;

  &:focus-visible {
    outline: 2px solid var(--theme);
  }
  ${props => props.hasBackButton && 'padding: 4px 4px 4px 0;'}
`;

export const ModalHeaderTextContainer = styled('div')`
  align-items: center;
  display: flex;
`;

export const ModalHideActionButton = styled('button')`
  background-color: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    background-color: #00000020;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--legible-element-color);
  }
`;

export const ModalHeaderActionButton = styled('button')<{ selected?: boolean }>`
  background-color: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  padding: 4px;
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled):not([aria-disabled='true']) {
    background-color: #00000020;
  }

  ${props => props.selected && 'background-color: #00000020;'}

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--legible-element-color);
  }

  &:disabled,
  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const ModalHeaderButtonsContainer = styled('div')`
  align-items: center;
  display: flex;
  justify-content: flex-end;
`;
